// frontend/src/pages/TransacoesPage/TransacoesPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout, Typography, Button, Table, Tag, Space, Tooltip, Modal as AntModal, Card, Row, Col, Statistic, Divider, Progress, Form, Input, InputNumber, Select, DatePicker, message, List, Alert } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './TransacoesPage.css';
import { 
    EditOutlined, 
    DeleteOutlined, 
    PlusOutlined, 
    CheckCircleOutlined, 
    ExclamationCircleOutlined, 
    FileTextOutlined, 
    CheckCircleFilled, 
    CalendarOutlined, 
    RollbackOutlined, 
    DollarCircleOutlined,
    WhatsAppOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    BankOutlined,
    CreditCardOutlined
} from '@ant-design/icons';
import { getTransacoes, deleteTransacao, marcarTransacaoComoPaga, reverterTransacaoParaPendente } from '../../services/transactionService';
import { getContas } from '../../services/accountService';
import { getCategorias } from '../../services/categoryService';
import { createRecorrenciaExcecao, confirmarOcorrenciaRecorrencia } from '../../services/recurrenceService';
import { getFaturaDetail } from '../../services/cardService';
import { useDataRefresh } from '../../contexts/DataRefreshContext';
import { useAuth } from '../../contexts/AuthContext'; 
import TransacaoModal from '../../components/TransacaoModal/TransacaoModal';
import SeletorDeMes from '../../components/SeletorDeMes/SeletorDeMes';
import PagarFaturaModal from '../../components/PagarFaturaModal/PagarFaturaModal';
import CobrancaModal from '../../components/CobrancaModal/CobrancaModal'; 
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import { getErrorMessage } from '../../utils/errorHandler';
import MobileLaunchSheet from '../../components/MobileLaunchSheet/MobileLaunchSheet';

dayjs.locale('pt-br');

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const formatarValor = (valor) => parseFloat(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Estilo para o hover dos cards de saldo
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
    .account-balance-item:hover {
        background-color: var(--color-surface-muted, #F8F4EC);
    }
`;
document.head.appendChild(styleSheet);

// --- MODAIS AUXILIARES RESPONSIVOS ---

const ConfirmacaoPagamentoModal = ({ isOpen, onClose, onSave, transacao, contas, isMobile }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const isReceita = transacao?.tipo === 'receita';
    const modalTitle = isReceita ? "Confirmar Recebimento" : "Confirmar Pagamento";
    const okText = isReceita ? "Confirmar Recebimento" : "Confirmar Pagamento";
    const contaLabel = isReceita ? "Creditar na Conta" : "Debitar da Conta";

    useEffect(() => {
        if (isOpen && transacao) {
            form.setFieldsValue({
                descricao: transacao.descricao,
                data_pagamento: dayjs(),
                valor: parseFloat(transacao.valor),
                conta_id: transacao.conta,
            });
        }
    }, [isOpen, transacao, form]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const payload = {
                data_pagamento: values.data_pagamento.format('YYYY-MM-DD'),
                valor: values.valor,
                conta_id: values.conta_id,
                descricao: values.descricao,
            };
            await marcarTransacaoComoPaga(transacao.id, payload);
            onSave();
        } catch (error) {
            AntModal.error({ 
                title: 'Erro', 
                content: getErrorMessage(error, `Ocorreu um erro ao confirmar o ${isReceita ? 'recebimento' : 'pagamento'}.`) 
            });
        } finally {
            setLoading(false);
        }
    };

    return isMobile ? (
        <MobileLaunchSheet
            open={isOpen}
            title={modalTitle}
            onCancel={onClose}
            onSave={handleSave}
            saveLabel="Confirmar"
            saveLoading={loading}
            cancelDisabled={loading}
        >
            <Form form={form} layout="vertical" size="large">
                <p>Confirme ou ajuste os dados da transação realizada.</p>
                <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <Input />
                </Form.Item>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="data_pagamento" label="Data Real" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="valor" label="Valor Real" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                            <InputNumber
                                style={{ width: '100%' }}
                                precision={2}
                                step={0.01}
                                inputMode="decimal"
                                formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                                parser={(value) => {
                                    if (!value) return null;
                                    const onlyNumbers = value.replace(/[^\d]/g, '');
                                    if (!onlyNumbers) return null;
                                    return parseInt(onlyNumbers, 10) / 100;
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="conta_id" label={contaLabel} rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <Select placeholder="Selecione a conta">
                        {(contas || []).map(c => (
                            <Option key={c.id} value={c.id}>
                                {isReceita
                                    ? c.nome
                                    : `${c.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.saldo_atual)})`}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </MobileLaunchSheet>
    ) : (
        <AntModal
            title={modalTitle}
            open={isOpen}
            onCancel={onClose}
            onOk={handleSave}
            confirmLoading={loading}
            okText={okText}
            cancelText="Cancelar"
            destroyOnClose
            width={520}
        >
            <Form form={form} layout="vertical">
                <p>Confirme ou ajuste os dados da transação realizada.</p>
                <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <Input />
                </Form.Item>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="data_pagamento" label="Data Real" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="valor" label="Valor Real" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                            <InputNumber
                                style={{ width: '100%' }}
                                precision={2}
                                step={0.01}
                                inputMode="decimal"
                                formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                                parser={(value) => {
                                    if (!value) return null;
                                    const onlyNumbers = value.replace(/[^\d]/g, '');
                                    if (!onlyNumbers) return null;
                                    return parseInt(onlyNumbers, 10) / 100;
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="conta_id" label={contaLabel} rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <Select placeholder="Selecione a conta">
                        {(contas || []).map(c => (
                            <Option key={c.id} value={c.id}>
                                {isReceita
                                    ? c.nome
                                    : `${c.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.saldo_atual)})`}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </AntModal>
    );
};

const ProjetadaEditModal = ({ isOpen, onClose, onSave, projetado, isMobile }) => {
    const [form] = Form.useForm();
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && projetado) {
            form.setFieldsValue({
                nova_descricao: projetado.descricao,
                novo_valor: parseFloat(projetado.valor),
                nova_data: dayjs(projetado.data_vencimento),
            });
            getCategorias().then(data => {
                const filteredCategorias = data.filter(c => c.tipo === projetado.tipo);
                setCategorias(filteredCategorias);
                const categoriaAtual = filteredCategorias.find(c => c.nome === projetado.categoria_nome);
                if (categoriaAtual) {
                    form.setFieldsValue({ nova_categoria: categoriaAtual.id });
                }
            });
        }
    }, [isOpen, projetado, form]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const payload = {
                recorrencia: projetado.recorrencia_id,
                data_original_ocorrencia: dayjs(projetado.data_original_ocorrencia).format('YYYY-MM-DD'),
                ...values,
                nova_data: values.nova_data ? values.nova_data.format('YYYY-MM-DD') : null,
            };
            await createRecorrenciaExcecao(payload);
            onSave();
        } catch (error) {
            console.error("Erro ao salvar exceção:", error);
            AntModal.error({ 
                title: 'Erro', 
                content: getErrorMessage(error, 'Não foi possível salvar a alteração.') 
            });
        } finally {
            setLoading(false);
        }
    };

    return isMobile ? (
        <MobileLaunchSheet
            open={isOpen}
            title="Editar Projetado"
            onCancel={onClose}
            onSave={handleSave}
            saveLabel="Salvar"
            saveLoading={loading}
            cancelDisabled={loading}
        >
            <Form form={form} layout="vertical" size="large">
                <p>Você está editando apenas a ocorrência de <strong>{projetado ? dayjs(projetado.data_vencimento).format('DD/MM/YYYY') : ''}</strong>. A regra original não será alterada.</p>
                <Form.Item name="nova_descricao" label="Descrição">
                    <Input />
                </Form.Item>
                <Form.Item name="novo_valor" label="Valor">
                    <InputNumber
                        style={{ width: '100%' }}
                        precision={2}
                        step={0.01}
                        inputMode="decimal"
                        formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                        parser={(value) => {
                            if (!value) return null;
                            const onlyNumbers = value.replace(/[^\d]/g, '');
                            if (!onlyNumbers) return null;
                            return parseInt(onlyNumbers, 10) / 100;
                        }}
                    />
                </Form.Item>
                <Form.Item name="nova_data" label="Nova Data">
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="nova_categoria" label="Categoria">
                    <Select placeholder="Selecione uma categoria">
                        {categorias.map(cat => <Option key={cat.id} value={cat.id}>{cat.nome}</Option>)}
                    </Select>
                </Form.Item>
            </Form>
        </MobileLaunchSheet>
    ) : (
        <AntModal
            title="Editar Lançamento Projetado"
            open={isOpen}
            onCancel={onClose}
            onOk={handleSave}
            confirmLoading={loading}
            okText="Salvar Alteração"
            cancelText="Cancelar"
            width={520}
        >
            <Form form={form} layout="vertical">
                <p>Você está editando apenas a ocorrência de <strong>{projetado ? dayjs(projetado.data_vencimento).format('DD/MM/YYYY') : ''}</strong>. A regra original não será alterada.</p>
                <Form.Item name="nova_descricao" label="Descrição">
                    <Input />
                </Form.Item>
                <Form.Item name="novo_valor" label="Valor">
                    <InputNumber
                        style={{ width: '100%' }}
                        precision={2}
                        step={0.01}
                        inputMode="decimal"
                        formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                        parser={(value) => {
                            if (!value) return null;
                            const onlyNumbers = value.replace(/[^\d]/g, '');
                            if (!onlyNumbers) return null;
                            return parseInt(onlyNumbers, 10) / 100;
                        }}
                    />
                </Form.Item>
                <Form.Item name="nova_data" label="Nova Data">
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="nova_categoria" label="Categoria">
                    <Select placeholder="Selecione uma categoria">
                        {categorias.map(cat => <Option key={cat.id} value={cat.id}>{cat.nome}</Option>)}
                    </Select>
                </Form.Item>
            </Form>
        </AntModal>
    );
};

const PagarProjetadoModal = ({ isOpen, onClose, onSave, projetado, contas, isMobile }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const isReceita = projetado?.tipo === 'receita';
    const modalTitle = isReceita ? 'Receber Lançamento' : 'Pagar Lançamento';
    const okText = isReceita ? 'Confirmar Recebimento' : 'Confirmar Pagamento';
    const dataLabel = isReceita ? 'Data Real do Recebimento' : 'Data Real do Pagamento';
    const confirmacaoTexto = isReceita ? 'Confirme os dados do recebimento realizado.' : 'Confirme os dados do pagamento realizado.';

    useEffect(() => {
        if (isOpen && projetado) {
            const contaProjetada = contas.find(c => c.nome === projetado.conta_nome);
            form.setFieldsValue({
                data_pagamento: dayjs(),
                valor: parseFloat(projetado.valor),
                descricao: projetado.descricao,
                conta_id: contaProjetada ? contaProjetada.id : null,
            });
        }
    }, [isOpen, projetado, form, contas]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const payload = {
                data_original_ocorrencia: dayjs(projetado.data_original_ocorrencia).format('YYYY-MM-DD'),
                descricao: values.descricao,
                valor: values.valor,
                data_pagamento: values.data_pagamento.format('YYYY-MM-DD'),
                conta_id: values.conta_id,
            };
            await confirmarOcorrenciaRecorrencia(projetado.recorrencia_id, payload);
            onSave();
        } catch (error) {
            console.error("Erro ao confirmar lançamento:", error);
            AntModal.error({ 
                title: 'Erro', 
                content: getErrorMessage(error, 'Não foi possível confirmar o lançamento.') 
            });
        } finally {
            setLoading(false);
        }
    };

    return isMobile ? (
        <MobileLaunchSheet
            open={isOpen}
            title={modalTitle}
            onCancel={onClose}
            onSave={handleSave}
            saveLabel="Confirmar"
            saveLoading={loading}
            cancelDisabled={loading}
        >
            <Form form={form} layout="vertical" size="large">
                <p>{confirmacaoTexto}</p>
                <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="data_pagamento" label={dataLabel} rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="valor" label="Valor Real" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <InputNumber
                        style={{ width: '100%' }}
                        precision={2}
                        step={0.01}
                        inputMode="decimal"
                        formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                        parser={(value) => {
                            if (!value) return null;
                            const onlyNumbers = value.replace(/[^\d]/g, '');
                            if (!onlyNumbers) return null;
                            return parseInt(onlyNumbers, 10) / 100;
                        }}
                    />
                </Form.Item>
                <Form.Item name="conta_id" label="Conta" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <Select placeholder="Selecione a conta">
                        {contas.map(c => (
                            <Option key={c.id} value={c.id}>
                                {`${c.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.saldo_atual)})`}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </MobileLaunchSheet>
    ) : (
        <AntModal
            title={modalTitle}
            open={isOpen}
            onCancel={onClose}
            onOk={handleSave}
            confirmLoading={loading}
            okText={okText}
            cancelText="Cancelar"
            width={520}
        >
            <Form form={form} layout="vertical">
                <p>{confirmacaoTexto}</p>
                <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="data_pagamento" label={dataLabel} rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="valor" label="Valor Real" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <InputNumber
                        style={{ width: '100%' }}
                        precision={2}
                        step={0.01}
                        inputMode="decimal"
                        formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                        parser={(value) => {
                            if (!value) return null;
                            const onlyNumbers = value.replace(/[^\d]/g, '');
                            if (!onlyNumbers) return null;
                            return parseInt(onlyNumbers, 10) / 100;
                        }}
                    />
                </Form.Item>
                <Form.Item name="conta_id" label="Conta" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                    <Select placeholder="Selecione a conta">
                        {contas.map(c => (
                            <Option key={c.id} value={c.id}>
                                {`${c.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.saldo_atual)})`}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </AntModal>
    );
};

// --- PÁGINA PRINCIPAL ---

function TransacoesPage() {
    const { user } = useAuth();
    const [itens, setItens] = useState([]);
    const [contas, setContas] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagarFaturaLoading, setPagarFaturaLoading] = useState(false);
    const [dataFiltro, setDataFiltro] = useState(new Date());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTransacao, setEditingTransacao] = useState(null);
    
    const [modal, modalContextHolder] = AntModal.useModal();
    const [messageApi, messageContextHolder] = message.useMessage();

    const [isProjetadaModalOpen, setIsProjetadaModalOpen] = useState(false);
    const [editingProjetado, setEditingProjetado] = useState(null);
    const [isPagarModalOpen, setIsPagarModalOpen] = useState(false);
    const [payingProjetado, setPayingProjetado] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmingTransacao, setConfirmingTransacao] = useState(null);
    const { refreshKey } = useDataRefresh();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isPagarFaturaModalOpen, setIsPagarFaturaModalOpen] = useState(false);
    const [faturaToPay, setFaturaToPay] = useState(null);

    const [isCobrancaModalOpen, setIsCobrancaModalOpen] = useState(false);
    const [transacaoCobranca, setTransacaoCobranca] = useState(null);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
    const [sorter, setSorter] = useState({ field: 'data_vencimento', order: 'ascend' });
    const [filters, setFilters] = useState(() => {
        const categoriaParam = searchParams.get('categoria');
        return {
            tipo: null,
            descricao: null,
            status: null,
            categoria_nome: categoriaParam ? [categoriaParam] : null,
            conta_nome: null,
        };
    });

    // Deep link vindo de uma notificação (ex.: "lançamentos sem categoria" do
    // Telegram): aplica o filtro de categoria uma vez e limpa a URL.
    useEffect(() => {
        if (searchParams.get('categoria')) {
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Estado para responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const carregarDados = useCallback(async () => {
        setLoading(true);
        try {
            const ano = dataFiltro.getFullYear();
            const mes = dataFiltro.getMonth() + 1;
            const params = { ano, mes };

            const [itensData, contasData, categoriasData] = await Promise.all([
                getTransacoes(params),
                getContas(),
                getCategorias()
            ]);

            setItens(itensData);
            setContas(contasData.results || contasData || []);
            setCategorias(categoriasData);
        } catch (error) {
            console.error("Falha ao buscar dados da página de transações:", error);
            AntModal.error({ 
                title: 'Erro', 
                content: getErrorMessage(error, 'Falha ao buscar dados. Tente novamente.') 
            });
        } finally {
            setLoading(false);
        }
    }, [dataFiltro]);

    useEffect(() => {
        carregarDados();
    }, [carregarDados, refreshKey]);

    useEffect(() => {
        const handleTransactionUpdate = () => {
            carregarDados();
        };
        window.addEventListener('transaction-updated', handleTransactionUpdate);
        return () => {
            window.removeEventListener('transaction-updated', handleTransactionUpdate);
        };
    }, [carregarDados]);

    const kpis = useMemo(() => {
        const saldoConsolidado = contas.reduce((acc, conta) => acc + parseFloat(conta.saldo_atual || 0), 0);

        const projecaoCalc = itens.reduce((acc, item) => {
            const valor = parseFloat(item.valor) || 0;
            const statusLower = item.status?.toLowerCase();
            const isPendente = statusLower === 'pendente' || statusLower === 'fechada' || statusLower === 'aberta' || item.item_type?.startsWith('transacao_projetada');

            if (isPendente) {
                if (item.tipo === 'receita') {
                    acc.aReceber += valor;
                } else if (item.tipo === 'despesa' || item.tipo === 'pagamento_fatura') {
                    acc.aPagar += valor;
                }
            }
            return acc;
        }, { aReceber: 0, aPagar: 0 });

        const itensAnalise = itens.filter(item => !item.is_transferencia);
        const analiseCalc = itensAnalise.reduce((acc, item) => {
            const valor = parseFloat(item.valor) || 0;
            const statusLower = item.status?.toLowerCase();
            const isPendente = statusLower === 'pendente' || statusLower === 'fechada' || statusLower === 'aberta' || item.item_type?.startsWith('transacao_projetada');

            if (item.tipo === 'receita') {
                acc.totalEntradas += valor;
                if (isPendente) acc.aReceber += valor;
            } else if (item.tipo === 'despesa' || item.tipo === 'pagamento_fatura') {
                acc.totalSaidas += valor;
                if (isPendente) acc.aPagar += valor;
            }
            return acc;
        }, { totalEntradas: 0, totalSaidas: 0, aReceber: 0, aPagar: 0 });

        return {
            saldoConsolidado,
            totalEntradas: analiseCalc.totalEntradas,
            totalSaidas: analiseCalc.totalSaidas,
            aReceberPendente: projecaoCalc.aReceber,
            aPagarPendente: projecaoCalc.aPagar,
            resultadoDoMes: analiseCalc.totalEntradas - analiseCalc.totalSaidas,
            saldoProjetado: saldoConsolidado + projecaoCalc.aReceber - projecaoCalc.aPagar,
            totalRecebido: analiseCalc.totalEntradas - analiseCalc.aReceber,
            totalPago: analiseCalc.totalSaidas - analiseCalc.aPagar,
        };
    }, [itens, contas]);

    const saldosPorConta = useMemo(() => {
        const activeContas = contas.filter(c => c.status === 'ATIVO');

        return activeContas.map(conta => {
            const saldoAtual = parseFloat(conta.saldo_atual || 0);
            const transacoesDaConta = itens.filter(item => item.conta_nome === conta.nome);

            const variacao = transacoesDaConta.reduce((acc, item) => {
                const valor = parseFloat(item.valor) || 0;
                const statusLower = item.status?.toLowerCase();
                const isPendente = statusLower === 'pendente' || statusLower === 'fechada' || statusLower === 'aberta' || item.item_type?.startsWith('transacao_projetada');

                if (isPendente) {
                    if (item.tipo === 'receita') {
                        acc.entradas += valor;
                    } else if (item.tipo === 'despesa' || item.tipo === 'pagamento_fatura') {
                        acc.saidas += valor;
                    }
                }
                return acc;
            }, { entradas: 0, saidas: 0 });

            const variacaoLiquida = variacao.entradas - variacao.saidas;
            const saldoPrevisto = saldoAtual + variacaoLiquida;

            return {
                id: conta.id,
                nome: conta.nome,
                saldoAtual,
                saldoPrevisto,
                variacaoLiquida,
            };
        });
    }, [itens, contas]);

    const handleTableChange = (pagination, newFilters, newSorter) => {
        setPagination(pagination);
        setSorter({ field: newSorter.field, order: newSorter.order });
        setFilters(prev => ({
            ...prev,
            status: (newFilters?.status && newFilters.status.length) ? newFilters.status : null,
            categoria_nome: (newFilters?.categoria_nome && newFilters.categoria_nome.length) ? newFilters.categoria_nome : null,
            conta_nome: (newFilters?.conta_nome && newFilters.conta_nome.length) ? newFilters.conta_nome : null,
        }));
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const processedData = useMemo(() => {
        let data = [...itens];

        if (filters.descricao) {
            try {
                const regex = new RegExp(filters.descricao, 'i');
                data = data.filter(item => item.descricao && regex.test(item.descricao));
            } catch (e) {
                console.error("Regex inválido:", e);
            }
        }
        if (filters.tipo) {
            data = data.filter(item => item.tipo === filters.tipo);
        }
        const statusList = Array.isArray(filters.status) ? filters.status : (filters.status ? [filters.status] : []);
        if (statusList.length > 0) {
            data = data.filter(item =>
                statusList.some(statusVal => {
                    if (statusVal === 'PAGO') return item.status === 'PAGO' || item.status === 'PAGA';
                    if (statusVal === 'PENDENTE') return item.status === 'PENDENTE';
                    if (statusVal === 'PROJETADO') return item.item_type?.startsWith('transacao_projetada');
                    if (statusVal === 'FATURA') return item.item_type === 'fatura';
                    return false;
                })
            );
        }
        const categoriaList = Array.isArray(filters.categoria_nome) ? filters.categoria_nome : (filters.categoria_nome ? [filters.categoria_nome] : []);
        if (categoriaList.length > 0) {
            data = data.filter(item => categoriaList.includes(item.categoria_nome));
        }
        const contaList = Array.isArray(filters.conta_nome) ? filters.conta_nome : (filters.conta_nome ? [filters.conta_nome] : []);
        if (contaList.length > 0) {
            data = data.filter(item => contaList.includes(item.cartao_nome || item.conta_nome));
        }

        if (sorter.field && sorter.order) {
            data.sort((a, b) => {
                let valA = a[sorter.field];
                let valB = b[sorter.field];

                if (sorter.field === 'data_vencimento') {
                    valA = dayjs(valA);
                    valB = dayjs(valB);
                }

                if (valA < valB) return sorter.order === 'ascend' ? -1 : 1;
                if (valA > valB) return sorter.order === 'ascend' ? 1 : -1;
                return 0;
            });
        }

        return data;
    }, [itens, filters, sorter]);

    const dataWithVisualGroups = useMemo(() => {
        if (sorter.field !== 'data_vencimento' || !sorter.order) {
            return processedData;
        }

        const dataWithInjectedRows = [];
        let lastDate = null;
        let dailyExpenses = 0;
        let dailyItemsCount = 0;

        processedData.forEach((item, index) => {
            const currentDate = dayjs(item.data_vencimento).format('YYYY-MM-DD');

            if (lastDate && currentDate !== lastDate) {
                if (dailyItemsCount > 1 && dailyExpenses > 0) {
                    dataWithInjectedRows.push({
                        id: `summary-${lastDate}`,
                        isSummary: true,
                        total: dailyExpenses,
                    });
                }
                dailyExpenses = 0;
                dailyItemsCount = 0;
            }

            if (!lastDate || currentDate !== lastDate) {
                dataWithInjectedRows.push({
                    id: `header-${currentDate}`,
                    isDateHeader: true,
                    date: currentDate,
                });
            }

            dataWithInjectedRows.push(item);

            if ((item.tipo === 'despesa' || item.tipo === 'pagamento_fatura') && !item.is_transferencia) {
                dailyExpenses += parseFloat(item.valor || 0);
            }
            dailyItemsCount++;
            lastDate = currentDate;

            if (index === processedData.length - 1) {
                if (dailyItemsCount > 1 && dailyExpenses > 0) {
                    dataWithInjectedRows.push({
                        id: `summary-${lastDate}`,
                        isSummary: true,
                        total: dailyExpenses,
                    });
                }
            }
        });

        return dataWithInjectedRows;

    }, [processedData, sorter]);

    const hasActiveFilters = !!(
        filters.descricao?.trim() ||
        filters.tipo ||
        (Array.isArray(filters.status) ? filters.status.length > 0 : !!filters.status) ||
        (Array.isArray(filters.categoria_nome) ? filters.categoria_nome.length > 0 : !!filters.categoria_nome) ||
        (Array.isArray(filters.conta_nome) ? filters.conta_nome.length > 0 : !!filters.conta_nome)
    );

    const filteredTotals = useMemo(() => {
        if (!hasActiveFilters) return null;
        let quantidade = 0;
        let totalEntradas = 0;
        let totalSaidas = 0;
        const itensFiltro = processedData.filter(item => !item.isDateHeader && !item.isSummary);
        itensFiltro.forEach(item => {
            quantidade += 1;
            if (item.is_transferencia) return;
            const valor = parseFloat(item.valor) || 0;
            if (item.tipo === 'receita') totalEntradas += valor;
            else if (item.tipo === 'despesa' || item.tipo === 'pagamento_fatura') totalSaidas += valor;
        });
        return {
            active: true,
            quantidade,
            totalEntradas,
            totalSaidas,
            resultado: totalEntradas - totalSaidas,
        };
    }, [hasActiveFilters, processedData]);

    const handleOpenModal = useCallback((transacao = null) => {
        if (transacao && transacao.item_type === 'fatura') {
            modal.warning({ title: 'Ação não permitida', content: 'A edição de faturas deve ser feita na página de detalhes do cartão.' });
            return;
        }
        if (transacao && transacao.item_type?.startsWith('transacao_projetada')) {
            setEditingProjetado(transacao);
            setIsProjetadaModalOpen(true);
            return;
        }
        setEditingTransacao(transacao);
        setIsModalOpen(true);
    }, [modal]);

    const handleCloseModal = () => { setEditingTransacao(null); setIsModalOpen(false); };

    const handleSave = (shouldClose = true, successMessage = null) => {
        carregarDados();
        if (successMessage) messageApi.success(successMessage);
        if (shouldClose) {
            handleCloseModal();
        }
    };

    const handleOpenPagarModal = useCallback((item) => {
        setPayingProjetado(item);
        setIsPagarModalOpen(true);
    }, []);

    const handleOpenPagarFaturaModal = useCallback(async (faturaRecord) => {
        setPagarFaturaLoading(true);
        try {
            const dataVencimento = dayjs(faturaRecord.data_vencimento);
            const ano = dataVencimento.year();
            const mes = dataVencimento.month() + 1;

            if (!faturaRecord.cartao_credito) {
                AntModal.error({ title: 'Erro de Dados', content: 'Não foi possível obter os detalhes do cartão para esta fatura.' });
                setPagarFaturaLoading(false);
                return;
            }

            const response = await getFaturaDetail(faturaRecord.cartao_credito, ano, mes);
            setFaturaToPay(response.fatura);
            setIsPagarFaturaModalOpen(true);
        } catch (error) {
            console.error("Erro ao buscar detalhes da fatura:", error);
            AntModal.error({ 
                title: 'Erro de Rede', 
                content: getErrorMessage(error, 'Não foi possível carregar os detalhes da fatura para pagamento.') 
            });
        } finally {
            setPagarFaturaLoading(false);
        }
    }, []);

    const handleDelete = useCallback((item) => {
        if (item.item_type === 'fatura') {
            modal.error({ title: 'Ação não permitida', content: 'A exclusão de faturas não é permitida por esta tela.' });
            return;
        }
        if (item.item_type?.startsWith('transacao_projetada')) {
            modal.confirm({
                title: 'Pular esta ocorrência?',
                content: 'Tem certeza que deseja pular o lançamento projetado deste dia? A regra de recorrência original não será alterada.',
                okText: 'Sim, pular',
                cancelText: 'Cancelar',
                onOk: async () => {
                    try {
                        await createRecorrenciaExcecao({
                            recorrencia: item.recorrencia_id,
                            data_original_ocorrencia: dayjs(item.data_original_ocorrencia).format('YYYY-MM-DD'),
                            pulada: true
                        });
                        messageApi.success('Ocorrência pulada com sucesso!');
                        carregarDados();
                    } catch (error) {
                        modal.error({ 
                            title: 'Erro', 
                            content: getErrorMessage(error, 'Ocorreu um erro ao tentar pular a ocorrência.') 
                        });
                    }
                }
            });
            return;
        }
        modal.confirm({
            title: 'Confirmar exclusão', content: 'Tem certeza que deseja excluir esta transação?',
            okText: 'Excluir', okType: 'danger', cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteTransacao(item.id);
                    messageApi.success('Transação excluída com sucesso!');
                    carregarDados();
                } catch (error) {
                    modal.error({ 
                        title: 'Erro', 
                        content: getErrorMessage(error, 'Ocorreu um erro ao tentar excluir a transação.') 
                    });
                }
            }
        });
    }, [modal, carregarDados, messageApi]);

    const handleMarcarComoPago = useCallback((transacao) => {
        setConfirmingTransacao(transacao);
        setIsConfirmModalOpen(true);
    }, []);

    const handleReverterParaPendente = useCallback(async (transacaoId) => {
        try {
            await reverterTransacaoParaPendente(transacaoId);
            messageApi.success('Transação revertida para pendente com sucesso.');
            carregarDados();
        } catch (error) {
            modal.error({ 
                title: 'Erro', 
                content: getErrorMessage(error, 'Ocorreu um erro ao reverter a transação.') 
            });
        }
    }, [modal, carregarDados, messageApi]);

    const formatarData = (dataStr) => {
        if (!dataStr) return '-';
        const data = new Date(dataStr);
        const dataUTC = new Date(data.getUTCFullYear(), data.getUTCMonth(), data.getUTCDate());
        return dataUTC.toLocaleDateString('pt-BR');
    };

    // Função auxiliar para renderizar botões de ação (reusável)
    const renderActionButtons = useCallback((record) => {
        if (record.isSummary || record.isDateHeader) return null;

        const wrapStyle = isMobile
            ? { display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 }
            : { display: 'flex', justifyContent: 'flex-end', gap: '2px' };

        if (record.item_type === 'fatura') {
            if (record.status !== 'PAGA') {
                return (
                    <div style={wrapStyle}>
                        {isMobile ? (
                            <Button size="small" icon={<DollarCircleOutlined />} onClick={() => handleOpenPagarFaturaModal(record)} type="text" style={{ color: 'var(--ant-color-primary)' }} loading={pagarFaturaLoading} disabled={loading}>Pagar Fatura</Button>
                        ) : (
                            <Tooltip title="Pagar Fatura">
                                <Button icon={<DollarCircleOutlined />} onClick={() => handleOpenPagarFaturaModal(record)} type="text" style={{ color: 'var(--ant-color-primary)' }} loading={pagarFaturaLoading} disabled={loading} />
                            </Tooltip>
                        )}
                    </div>
                );
            }
            return null;
        }

        if (record.tipo === 'pagamento_fatura') return null;

        const isProjetado = record.item_type?.startsWith('transacao_projetada');
        const statusNormalizado = record.status ? record.status.toUpperCase() : '';
        const isPendente = statusNormalizado === 'PENDENTE';
        const isPago = statusNormalizado === 'PAGO';
        const showCobranca = record.tipo === 'receita' && record.eh_cobranca && isPendente;

        if (isProjetado) {
            return (
                <div style={wrapStyle}>
                    {showCobranca && (isMobile ? <Button size="small" icon={<WhatsAppOutlined />} onClick={() => { setTransacaoCobranca(record); setIsCobrancaModalOpen(true); }} type="text" style={{ color: '#25D366' }} disabled={loading}>Cobrança</Button> : <Tooltip title="Gerar Cobrança"><Button icon={<WhatsAppOutlined />} onClick={() => { setTransacaoCobranca(record); setIsCobrancaModalOpen(true); }} type="text" style={{ color: '#25D366' }} disabled={loading} /></Tooltip>)}
                    {isMobile ? <Button size="small" icon={<CheckCircleFilled />} onClick={() => handleOpenPagarModal(record)} type="text" style={{ color: 'var(--ant-color-success)' }} disabled={loading}>{record.tipo === 'receita' ? 'Receber' : 'Pagar'}</Button> : <Tooltip title={record.tipo === 'receita' ? 'Receber' : 'Pagar'}><Button icon={<CheckCircleFilled />} onClick={() => handleOpenPagarModal(record)} type="text" style={{ color: 'var(--ant-color-success)' }} disabled={loading} /></Tooltip>}
                    {isMobile ? <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} type="text" disabled={loading}>Editar</Button> : <Tooltip title="Editar"><Button icon={<EditOutlined />} onClick={() => handleOpenModal(record)} type="text" disabled={loading} /></Tooltip>}
                    {isMobile ? <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} type="text" danger disabled={loading}>Pular</Button> : <Tooltip title="Pular"><Button icon={<DeleteOutlined />} onClick={() => handleDelete(record)} type="text" danger disabled={loading} /></Tooltip>}
                </div>
            );
        }

        return (
            <div style={wrapStyle}>
                {showCobranca && (isMobile ? <Button size="small" icon={<WhatsAppOutlined />} onClick={() => { setTransacaoCobranca(record); setIsCobrancaModalOpen(true); }} type="text" style={{ color: '#25D366' }} disabled={loading}>Cobrança</Button> : <Tooltip title="Gerar Cobrança"><Button icon={<WhatsAppOutlined />} onClick={() => { setTransacaoCobranca(record); setIsCobrancaModalOpen(true); }} type="text" style={{ color: '#25D366' }} disabled={loading} /></Tooltip>)}
                {isPendente && !record.is_transferencia && (isMobile ? <Button size="small" icon={<CheckCircleFilled />} onClick={() => handleMarcarComoPago(record)} type="text" style={{ color: 'var(--ant-color-success)' }} disabled={loading}>Confirmar</Button> : <Tooltip title={record.tipo === 'receita' ? 'Receber' : 'Pagar'}><Button icon={<CheckCircleFilled />} onClick={() => handleMarcarComoPago(record)} type="text" style={{ color: 'var(--ant-color-success)' }} disabled={loading} /></Tooltip>)}
                {isPago && !record.is_transferencia && (isMobile ? <Button size="small" icon={<RollbackOutlined />} onClick={() => handleReverterParaPendente(record.id)} type="text" disabled={loading}>Reverter</Button> : <Tooltip title="Reverter"><Button icon={<RollbackOutlined />} onClick={() => handleReverterParaPendente(record.id)} type="text" disabled={loading} /></Tooltip>)}
                {isMobile ? <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} type="text" disabled={loading}>Editar</Button> : <Tooltip title="Editar"><Button icon={<EditOutlined />} onClick={() => handleOpenModal(record)} type="text" disabled={loading} /></Tooltip>}
                {isMobile ? <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} type="text" danger disabled={loading}>Excluir</Button> : <Tooltip title="Excluir"><Button icon={<DeleteOutlined />} onClick={() => handleDelete(record)} type="text" danger disabled={loading} /></Tooltip>}
            </div>
        );
    }, [
        isMobile,
        loading,
        pagarFaturaLoading,
        handleOpenPagarFaturaModal,
        handleOpenPagarModal,
        handleOpenModal,
        handleDelete,
        handleMarcarComoPago,
        handleReverterParaPendente,
    ]);

    // --- COLUNAS (DESKTOP) ---
    const columns = useMemo(() => [
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 90,
            filteredValue: Array.isArray(filters.status) ? filters.status : (filters.status ? [filters.status] : undefined),
            filters: [
                { text: 'Pago/Recebido', value: 'PAGO' },
                { text: 'Pendente', value: 'PENDENTE' },
                { text: 'Projetado', value: 'PROJETADO' },
                { text: 'Fatura', value: 'FATURA' },
            ],
            onFilter: (value, record) => {
                if (record.isSummary || record.isDateHeader) return false;
                if (value === 'PAGO') return record.status === 'PAGO' || record.status === 'PAGA';
                if (value === 'PENDENTE') return record.status === 'PENDENTE';
                if (value === 'PROJETADO') return record.item_type?.startsWith('transacao_projetada');
                if (value === 'FATURA') return record.item_type === 'fatura';
                return true;
            },
            onCell: (record) => {
                if (record.isDateHeader) return { colSpan: 7 };
                if (record.isSummary) return { colSpan: 5 };
                return { colSpan: 1 };
            },
            render: (status, record) => {
                if (record.isDateHeader) {
                    return {
                        children: (
                            <Text strong style={{ textTransform: 'capitalize' }}>
                                {dayjs(record.date).format('dddd, DD [de] MMMM [de] YYYY')}
                            </Text>
                        ),
                        props: { style: { background: 'var(--color-surface-muted, #F8F4EC)', padding: '8px 16px', textAlign: 'center' } },
                    };
                }
                if (record.isSummary) {
                    return {
                        children: <Text strong style={{ float: 'right', paddingRight: '16px' }}>Total de Despesas do Dia:</Text>,
                        props: { style: { background: 'var(--ant-table-summary-row-bg)' } },
                    };
                }
                const isFatura = record.item_type === 'fatura';
                const isProjetado = record.item_type?.startsWith('transacao_projetada');
                const statusLower = status?.toLowerCase();
                const colorMapping = {
                    pago: 'success',
                    paga: 'success',
                    pendente: 'warning',
                    fechada: 'error',
                    aberta: 'processing'
                };
                const icon = isFatura ? <FileTextOutlined /> : (isProjetado ? <CalendarOutlined /> : (statusLower === 'pago' || statusLower === 'paga' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />));

                let displayText = status;
                if (record.tipo === 'receita' && (status?.toUpperCase() === 'PAGO' || status?.toUpperCase() === 'PAGA')) {
                    displayText = 'RECEBIDO';
                }

                return <Tag color={colorMapping[statusLower] || 'default'} icon={icon}>{displayText}</Tag>;
            }
        },
        {
            title: 'Vencimento',
            dataIndex: 'data_vencimento',
            key: 'data_vencimento',
            sorter: true,
            defaultSortOrder: 'ascend',
            width: 120,
            onCell: (record) => ({ colSpan: record.isSummary || record.isDateHeader ? 0 : 1 }),
            render: (text, record) => {
                const dataVencimento = formatarData(text);
                const dataPagamento = formatarData(record.data_pagamento);
                const statusLower = record.status?.toLowerCase();
                const isPagoEmDiaDiferente = (statusLower === 'pago' || statusLower === 'paga') && record.data_pagamento && record.data_pagamento !== record.data_vencimento;

                if (isPagoEmDiaDiferente) {
                    const tooltipTitle = `Pago em: ${dataPagamento}`;
                    return (
                        <Tooltip title={tooltipTitle}>
                            <span style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                {dataVencimento} <CheckCircleFilled style={{ color: 'var(--ant-color-success)', fontSize: '12px', marginLeft: '4px' }} />
                            </span>
                        </Tooltip>
                    );
                }
                return <span style={{ whiteSpace: 'nowrap' }}>{dataVencimento}</span>;
            }
        },
        {
            title: 'Descrição',
            dataIndex: 'descricao',
            key: 'descricao',
            sorter: true,
            ellipsis: true,
            onCell: (record) => ({ colSpan: record.isSummary || record.isDateHeader ? 0 : 1 }),
            render: (text, record) => (
                <span>
                    {text}
                    {record.tipo === 'receita' && record.eh_cobranca ? (
                        <Tag color="green" style={{ marginLeft: 8, fontSize: 11, fontWeight: 600 }}>Cobrança</Tag>
                    ) : null}
                </span>
            ),
        },
        {
            title: 'Categoria',
            dataIndex: 'categoria_nome',
            key: 'categoria_nome',
            width: 120,
            ellipsis: true,
            filteredValue: Array.isArray(filters.categoria_nome) ? filters.categoria_nome : (filters.categoria_nome ? [filters.categoria_nome] : undefined),
            filters: [...new Set(categorias.map(c => c.nome))].map(nome => ({ text: nome, value: nome })),
            onFilter: (value, record) => record.categoria_nome === value,
            onCell: (record) => ({ colSpan: record.isSummary || record.isDateHeader ? 0 : 1 }),
            render: (text) => text || 'N/A'
        },
        {
            title: 'Conta',
            dataIndex: 'conta_nome',
            key: 'conta_nome',
            width: 120,
            ellipsis: true,
            filteredValue: Array.isArray(filters.conta_nome) ? filters.conta_nome : (filters.conta_nome ? [filters.conta_nome] : undefined),
            filters: [...new Set(itens.map(i => i.cartao_nome || i.conta_nome).filter(Boolean))]
                .map(name => ({ text: name, value: name })),
            onFilter: (value, record) => (record.cartao_nome || record.conta_nome) === value,
            onCell: (record) => ({ colSpan: record.isSummary || record.isDateHeader ? 0 : 1 }),
            render: (text, record) => record.cartao_nome || text
        },
        {
            title: 'Valor',
            dataIndex: 'valor',
            key: 'valor',
            align: 'right',
            sorter: true,
            width: 110,
            onCell: (record) => ({ colSpan: record.isDateHeader ? 0 : 1 }),
            render: (valor, record) => {
                if (record.isSummary) {
                    return {
                        children: <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: '#D64545' }}>{formatarValor(record.total)}</span>,
                        props: { style: { background: 'var(--ant-table-summary-row-bg)' } },
                    };
                }
                const color = record.tipo === 'receita' ? '#2F9E6F' : '#D64545';
                return (
                    <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: color }}>
                        {formatarValor(valor)}
                    </span>
                );
            }
        },
        {
            title: 'Ações',
            key: 'acoes',
            align: 'right',
            width: 110,
            onCell: (record) => ({ colSpan: record.isDateHeader || record.isSummary ? 0 : 1 }),
            render: (_, record) => renderActionButtons(record)
        }
    ], [categorias, itens, filters, loading, pagarFaturaLoading, handleOpenModal, handleDelete, handleMarcarComoPago, handleOpenPagarModal, handleReverterParaPendente, handleOpenPagarFaturaModal, renderActionButtons]);

    return (
        <Content style={{ padding: isMobile ? '16px' : '24px' }}>
            {modalContextHolder}
            {messageContextHolder}
            
            {/* Header com Seletor de Data */}
            <Card style={{ marginBottom: '24px' }} bodyStyle={{ padding: isMobile ? '16px' : '24px' }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <Title level={2} style={{ margin: 0 }}>Transações</Title>
                    </Col>
                    <Col xs={24} md={12} style={{ display: 'flex', gap: 16, justifyContent: isMobile ? 'flex-start' : 'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
                        <div style={{ width: isMobile ? '100%' : 'auto' }}>
                            <SeletorDeMes value={dataFiltro} onChange={setDataFiltro} />
                        </div>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()} disabled={loading} block={isMobile} size={isMobile ? 'large' : 'middle'}>
                            Adicionar
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Row gutter={[24, 24]}>
                {/* Coluna Principal: Lista de Transações */}
                <Col xs={24} lg={17} order={isMobile ? 2 : 1}>
                    <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12} md={10}>
                                <Input.Search
                                    placeholder="Buscar por descrição..."
                                    allowClear
                                    onChange={(e) => handleFilterChange('descricao', e.target.value)}
                                    size={isMobile ? 'large' : 'middle'}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Filtrar por tipo"
                                    allowClear
                                    style={{ width: '100%' }}
                                    onChange={(value) => handleFilterChange('tipo', value)}
                                    size={isMobile ? 'large' : 'middle'}
                                >
                                    <Option value="receita">Receita</Option>
                                    <Option value="despesa">Despesa</Option>
                                </Select>
                            </Col>
                            {isMobile && (
                                <Col xs={24}>
                                    <Select
                                        placeholder="Status"
                                        allowClear
                                        style={{ width: '100%' }}
                                        value={filters.status === null || filters.status === undefined ? '' : (Array.isArray(filters.status) ? filters.status[0] : filters.status)}
                                        onChange={(value) => handleFilterChange('status', value === '' ? null : (value ? [value] : null))}
                                        size="large"
                                    >
                                        <Option value="">Todas</Option>
                                        <Option value="PAGO">Pago/Recebido</Option>
                                        <Option value="PENDENTE">Pendente</Option>
                                        <Option value="PROJETADO">Projetado</Option>
                                        <Option value="FATURA">Fatura</Option>
                                    </Select>
                                </Col>
                            )}
                        </Row>

                        {filteredTotals?.active && (
                            <Alert
                                type="info"
                                showIcon
                                message={
                                    <Row gutter={[24, 8]} align="middle">
                                        <Col>
                                            <Text strong>{filteredTotals.quantidade} {filteredTotals.quantidade === 1 ? 'item' : 'itens'} no filtro</Text>
                                        </Col>
                                        <Col>
                                            <Text type="secondary">Entradas: </Text>
                                            <Text style={{ color: '#2F9E6F', fontWeight: 500 }}>{formatarValor(filteredTotals.totalEntradas)}</Text>
                                        </Col>
                                        <Col>
                                            <Text type="secondary">Saídas: </Text>
                                            <Text style={{ color: '#D64545', fontWeight: 500 }}>{formatarValor(filteredTotals.totalSaidas)}</Text>
                                        </Col>
                                        <Col>
                                            <Text type="secondary">Resultado: </Text>
                                            <Text strong style={{ color: filteredTotals.resultado >= 0 ? '#2F9E6F' : '#D64545' }}>{formatarValor(filteredTotals.resultado)}</Text>
                                        </Col>
                                    </Row>
                                }
                                style={{ marginBottom: 24 }}
                            />
                        )}

                        {/* LISTA MOBILE OU TABELA DESKTOP */}
                        {isMobile ? (
                            <List
                                dataSource={dataWithVisualGroups}
                                loading={loading}
                                locale={{
                                    emptyText: (
                                        <EmptyStateGuide
                                            title="Nenhuma transação neste período."
                                            description={
                                                contas.length === 0
                                                    ? 'Cadastre uma conta ou cartão para começar a lançar receitas e despesas.'
                                                    : 'Lance uma receita ou despesa, ou ajuste o período nos filtros.'
                                            }
                                            actionLabel={contas.length === 0 ? 'Ir para Contas' : 'Novo lançamento'}
                                            onAction={contas.length === 0 ? () => navigate('/contas') : () => handleOpenModal()}
                                            icon={contas.length === 0 ? null : undefined}
                                        />
                                    ),
                                }}
                                renderItem={(item) => {
                                    // Renderiza cabeçalhos de data e sumários de forma especial
                                    if (item.isDateHeader) {
                                        return (
                                            <div style={{ background: 'var(--color-surface-muted, #F8F4EC)', padding: '12px 14px', fontWeight: 700, color: 'var(--color-text-primary, #1F4F4F)', marginTop: 20, marginBottom: 4, borderRadius: 12, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <CalendarOutlined style={{ fontSize: 18, color: '#1F4F4F' }} />
                                                {dayjs(item.date).format('dddd, DD [de] MMMM')}
                                            </div>
                                        );
                                    }
                                    if (item.isSummary) {
                                        return (
                                            <div style={{ textAlign: 'right', padding: '8px 12px', color: '#D64545', fontWeight: 'bold', borderTop: '1px solid var(--color-border, #E8E2D6)' }}>
                                                Total do dia: {formatarValor(item.total)}
                                            </div>
                                        );
                                    }

                                    // Linha densa (sem card por item)
                                    const color = item.tipo === 'receita' ? '#2F9E6F' : '#D64545';
                                    const Icon = item.tipo === 'receita' ? ArrowUpOutlined : ArrowDownOutlined;
                                    
                                    return (
                                        <div className="tx-dense-row">
                                            <div className="tx-dense-main">
                                                <div className="tx-dense-left">
                                                    <Text strong className="tx-dense-title">
                                                        {item.descricao}
                                                        {item.tipo === 'receita' && item.eh_cobranca ? (
                                                            <Tag color="green" className="tx-dense-tag">Cobrança</Tag>
                                                        ) : null}
                                                    </Text>
                                                    <div className="tx-dense-meta">
                                                        <span>{item.categoria_nome}</span>
                                                        <span>·</span>
                                                        <span>{item.cartao_nome || item.conta_nome}</span>
                                                    </div>
                                                </div>
                                                <div className="tx-dense-right">
                                                    <Text strong style={{ color, fontVariantNumeric: 'tabular-nums' }} className="tx-dense-value">
                                                        <Icon /> {formatarValor(item.valor)}
                                                    </Text>
                                                    {(() => {
                                                        const statusUpper = (item.status || '').toUpperCase();
                                                        const isPagoRecebido = statusUpper === 'PAGO' || statusUpper === 'PAGA';
                                                        const isPendente = statusUpper === 'PENDENTE';
                                                        const statusLabel = item.tipo === 'receita' && isPagoRecebido ? 'RECEBIDO' : item.status;
                                                        const tagColor = isPagoRecebido ? 'success' : isPendente ? 'gold' : 'default';
                                                        return <Tag color={tagColor} className="tx-dense-status">{statusLabel}</Tag>;
                                                    })()}
                                                </div>
                                            </div>
                                            <div className="tx-dense-actions">
                                                {renderActionButtons(item)}
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={dataWithVisualGroups}
                                loading={loading}
                                rowKey="id"
                                size="small"
                                scroll={{ x: 700 }}
                                pagination={{
                                    ...pagination,
                                    total: dataWithVisualGroups.length,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['25', '50', '100'],
                                }}
                                onChange={handleTableChange}
                                locale={{
                                    emptyText: (
                                        <EmptyStateGuide
                                            title="Nenhuma transação ou fatura neste período."
                                            description={
                                                contas.length === 0
                                                    ? 'Cadastre uma conta ou cartão para começar a lançar receitas e despesas.'
                                                    : 'Lance uma receita ou despesa, ou ajuste o período nos filtros.'
                                            }
                                            actionLabel={contas.length === 0 ? 'Ir para Contas' : 'Novo lançamento'}
                                            onAction={contas.length === 0 ? () => navigate('/contas') : () => handleOpenModal()}
                                            icon={contas.length === 0 ? null : undefined}
                                        />
                                    ),
                                }}
                            />
                        )}
                    </Card>
                </Col>

                {/* Coluna Lateral: resumo compacto */}
                <Col xs={24} lg={7} order={isMobile ? 1 : 2}>
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Card className="desempenho-hero-card" bordered={false}>
                            <div className="desempenho-hero-label">
                                <span>Saldo previsto (todas as contas)</span>
                            </div>
                            <div className={`desempenho-hero-value${kpis.saldoProjetado < 0 ? ' is-negative' : ''}`}>
                                {formatarValor(kpis.saldoProjetado)}
                            </div>
                            <div style={{ marginTop: 10, color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                                Atual: {formatarValor(kpis.saldoConsolidado)}
                            </div>
                        </Card>

                        <Card className="desempenho-kpi-card" bordered={false} bodyStyle={{ padding: 16 }}>
                            <Text strong style={{ display: 'block', marginBottom: 12, color: 'var(--color-text-primary)' }}>
                                Resumo do mês
                            </Text>
                            <Space direction="vertical" style={{ width: '100%' }} size={8}>
                                <Row justify="space-between">
                                    <Col><Text type="secondary">Entradas</Text></Col>
                                    <Col><Text style={{ color: '#2F9E6F', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(kpis.totalEntradas)}</Text></Col>
                                </Row>
                                <Row justify="space-between">
                                    <Col><Text type="secondary">Saídas</Text></Col>
                                    <Col><Text style={{ color: '#D64545', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(kpis.totalSaidas)}</Text></Col>
                                </Row>
                                <Divider style={{ margin: '4px 0' }} />
                                <Row justify="space-between">
                                    <Col><Text strong>Resultado</Text></Col>
                                    <Col>
                                        <Text strong style={{ color: kpis.resultadoDoMes >= 0 ? '#2F9E6F' : '#D64545', fontVariantNumeric: 'tabular-nums' }}>
                                            {formatarValor(kpis.resultadoDoMes)}
                                        </Text>
                                    </Col>
                                </Row>
                                {!isMobile && (
                                    <>
                                        <Divider style={{ margin: '8px 0' }} />
                                        <div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Receitas recebidas</Text>
                                            <Progress
                                                percent={kpis.totalEntradas > 0 ? (kpis.totalRecebido / kpis.totalEntradas) * 100 : 0}
                                                strokeColor="#50B0A0"
                                                trailColor="var(--color-surface-muted, #F8F4EC)"
                                                format={(percent) => `${percent.toFixed(0)}%`}
                                                size="small"
                                            />
                                        </div>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Despesas pagas</Text>
                                            <Progress
                                                percent={kpis.totalSaidas > 0 ? (kpis.totalPago / kpis.totalSaidas) * 100 : 0}
                                                strokeColor="#D64545"
                                                trailColor="var(--color-surface-muted, #F8F4EC)"
                                                format={(percent) => `${percent.toFixed(0)}%`}
                                                size="small"
                                            />
                                        </div>
                                    </>
                                )}
                            </Space>
                        </Card>

                        <Card
                            className="desempenho-kpi-card"
                            bordered={false}
                            title={<span style={{ fontSize: 14, fontWeight: 600 }}>Saldos das contas</span>}
                            bodyStyle={{ padding: 0 }}
                        >
                            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                                {saldosPorConta.length > 0 ? saldosPorConta.map((conta, index) => (
                                    <div
                                        key={conta.id}
                                        onClick={() => navigate(`/contas/${conta.id}`)}
                                        style={{ cursor: 'pointer', padding: '12px 16px', borderBottom: index < saldosPorConta.length - 1 ? '1px solid var(--color-border, #E8E2D6)' : 'none' }}
                                    >
                                        <Row justify="space-between" align="middle">
                                            <Col><Text strong style={{ fontSize: 13 }}>{conta.nome}</Text></Col>
                                            <Col>
                                                <Tag color={conta.variacaoLiquida > 0 ? 'success' : conta.variacaoLiquida < 0 ? 'error' : 'default'} style={{ margin: 0 }}>
                                                    {conta.variacaoLiquida >= 0 ? '+' : ''}{formatarValor(conta.variacaoLiquida)}
                                                </Tag>
                                            </Col>
                                        </Row>
                                        <Row justify="space-between" style={{ marginTop: 6 }}>
                                            <Col>
                                                <Text type="secondary" style={{ fontSize: 11 }}>Atual</Text>
                                                <div style={{ fontSize: 13, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(conta.saldoAtual)}</div>
                                            </Col>
                                            <Col style={{ textAlign: 'right' }}>
                                                <Text type="secondary" style={{ fontSize: 11 }}>Previsto</Text>
                                                <div style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(conta.saldoPrevisto)}</div>
                                            </Col>
                                        </Row>
                                    </div>
                                )) : (
                                    <EmptyStateGuide
                                        title="Nenhuma conta ativa encontrada."
                                        description="Cadastre contas para ver saldos e variações aqui."
                                        actionLabel="Ir para Contas"
                                        onAction={() => navigate('/contas')}
                                        icon={null}
                                    />
                                )}
                            </div>
                        </Card>
                    </Space>
                </Col>
            </Row>

            <TransacaoModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                transacaoToEdit={editingTransacao}
                contas={contas}
                allCategorias={categorias}
                isMobile={isMobile}
            />
            {confirmingTransacao && (
                <ConfirmacaoPagamentoModal
                    isOpen={isConfirmModalOpen}
                    onClose={() => setIsConfirmModalOpen(false)}
                    onSave={() => { 
                        setIsConfirmModalOpen(false); 
                        carregarDados(); 
                        messageApi.success('Transação marcada como realizada com sucesso!');
                    }}
                    transacao={confirmingTransacao}
                    contas={contas}
                    isMobile={isMobile}
                />
            )}
            {editingProjetado && (
                <ProjetadaEditModal
                    isOpen={isProjetadaModalOpen}
                    onClose={() => setIsProjetadaModalOpen(false)}
                    onSave={() => { 
                        setIsProjetadaModalOpen(false); 
                        carregarDados();
                        messageApi.success('Lançamento projetado atualizado!'); 
                    }}
                    projetado={editingProjetado}
                    isMobile={isMobile}
                />
            )}
            {payingProjetado && (
                <PagarProjetadoModal
                    isOpen={isPagarModalOpen}
                    onClose={() => setIsPagarModalOpen(false)}
                    onSave={() => { 
                        setIsPagarModalOpen(false);
                        carregarDados();
                        messageApi.success(payingProjetado?.tipo === 'receita' ? 'Recebimento de projeção confirmado!' : 'Pagamento de projeção confirmado!');
                    }}
                    projetado={payingProjetado}
                    contas={contas}
                    isMobile={isMobile}
                />
            )}
            <PagarFaturaModal
                isOpen={isPagarFaturaModalOpen}
                onClose={() => setIsPagarFaturaModalOpen(false)}
                onSave={() => {
                    setIsPagarFaturaModalOpen(false);
                    carregarDados();
                    messageApi.success('Pagamento de fatura realizado com sucesso!');
                }}
                fatura={faturaToPay}
                isMobile={isMobile}
            />
            <CobrancaModal
                visible={isCobrancaModalOpen}
                onClose={() => setIsCobrancaModalOpen(false)}
                transacao={transacaoCobranca}
                userPix={user?.chave_pix_padrao}
                userBanco={user?.banco_preferencia}
                isMobile={isMobile}
            />
        </Content>
    );
}

export default TransacoesPage;